import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { apiService } from "../../api/services/apiService";
import { useAuth } from "../../contexts/AuthContext";
import type { EntityPmgAssignment, EntityType, ProductManagementGroup } from "../../types";
import { isProductManager, isTenantAdmin } from "../../utils/appRoles";

interface PmgAssignmentSectionProps {
  entityUuid: string;
  entityType: EntityType;
  onAccessDenied?: (denied: boolean) => void;
}

const PmgAssignmentSection: React.FC<PmgAssignmentSectionProps> = ({
  entityUuid,
  entityType,
  onAccessDenied,
}) => {
  const { user } = useAuth();
  const userEmail = user?.email?.toLowerCase() ?? "";

  const [assignments, setAssignments] = useState<EntityPmgAssignment[]>([]);
  const [groups, setGroups] = useState<ProductManagementGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<ProductManagementGroup | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [assignmentList, groupList] = await Promise.all([
        apiService.getEntityPmgAssignments(entityUuid),
        apiService.getProductManagementGroups(),
      ]);
      setAssignments(assignmentList);
      setGroups(groupList);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load group assignments");
    } finally {
      setLoading(false);
    }
  }, [entityUuid]);

  useEffect(() => {
    void load();
  }, [load]);

  const canModifyAssignments = useMemo(() => {
    if (isTenantAdmin(user?.roles)) return true;
    return groups.some(
      (g) =>
        g.owners.some((o) => o.toLowerCase() === userEmail) &&
        assignments.some((a) => a.pmgUuid === g.uuid),
    ) || groups.some((g) => g.owners.some((o) => o.toLowerCase() === userEmail));
  }, [assignments, groups, user?.roles, userEmail]);

  const canEditAsset = useMemo(() => {
    if (assignments.length === 0) {
      return isTenantAdmin(user?.roles) || isProductManager(user?.roles);
    }
    if (isTenantAdmin(user?.roles)) return true;
    return assignments.some((assignment) => {
      const group = groups.find((g) => g.uuid === assignment.pmgUuid);
      if (!group) return false;
      return (
        group.owners.some((o) => o.toLowerCase() === userEmail) ||
        group.members.some((m) => m.toLowerCase() === userEmail)
      );
    });
  }, [assignments, groups, user?.roles, userEmail]);

  useEffect(() => {
    onAccessDenied?.(!canEditAsset);
  }, [canEditAsset, onAccessDenied]);

  const handleAssign = async () => {
    if (!selectedGroup) return;
    setSaving(true);
    setError(null);
    try {
      await apiService.assignPmgToEntity({
        pmgUuid: selectedGroup.uuid,
        entityUuid,
        entityType,
      });
      setSelectedGroup(null);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to assign group");
    } finally {
      setSaving(false);
    }
  };

  const handleUnassign = async (pmgUuid: string) => {
    setSaving(true);
    setError(null);
    try {
      await apiService.unassignPmgFromEntity(pmgUuid, entityUuid);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to unassign group");
    } finally {
      setSaving(false);
    }
  };

  const assignableGroups = groups.filter(
    (g) =>
      (isTenantAdmin(user?.roles) || g.owners.some((o) => o.toLowerCase() === userEmail)) &&
      !assignments.some((a) => a.pmgUuid === g.uuid),
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Loading management groups…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Management Group
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {!canEditAsset && assignments.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          This asset is managed by{" "}
          {assignments.map((a) => a.pmgName).join(", ")}. Contact a group owner for edit access.
        </Alert>
      )}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
        {assignments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No management group assigned.
          </Typography>
        ) : (
          assignments.map((a) => (
            <Chip
              key={a.pmgUuid}
              label={a.pmgName}
              size="small"
              onDelete={
                canModifyAssignments ? () => void handleUnassign(a.pmgUuid) : undefined
              }
              disabled={saving}
            />
          ))
        )}
      </Box>
      {canModifyAssignments && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <Autocomplete
            options={assignableGroups}
            getOptionLabel={(g) => g.name}
            value={selectedGroup}
            onChange={(_e, value) => setSelectedGroup(value)}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Assign group" placeholder="Select group" />
            )}
            sx={{ minWidth: 220, flex: 1 }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => void handleAssign()}
            disabled={!selectedGroup || saving}
          >
            Assign
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PmgAssignmentSection;
